import * as Yup from 'yup';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, NavLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import UserContext from '../../context/UserContext/UserContext';
import AuthService from '../../services/AuthService/AuthService';
import PasswordField from '../../components/PasswordField/PasswordField';
import CustomInput from '../../components/CustomInput/CustomInput';
import Button from '../../components/Button/Button';
import AuthLayout from '../../components/AuthLayout/AuthLayout';
import AuthErrorAlert from '../../components/AuthLayout/AuthErrorAlert';

interface FormValues {
  email: string;
  password: string;
}

const CONTACT_URL = 'https://www.japeto.ai/contact-us/';
const INLINE_LINK = 'font-bold text-primary underline-offset-4 hover:underline';

export default function LoginPage(): React.ReactElement {
  const { setUser } = React.useContext(UserContext);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    undefined
  );

  const navigate = useNavigate();

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required('Email is required')
      .email('Email must be a valid email address'),
    password: Yup.string().required('Password is required')
  });

  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema)
  });

  const getRedirectPath = (authStatus: { mfaComplete?: boolean }): string => {
    if (!authStatus.mfaComplete) return '/2fa';
    return '/chats';
  };

  const onSubmit = (data: FormValues): void => {
    setIsLoading(true);
    AuthService.login(data.email, data.password)
      .then(
        (loginResponse) => {
          setUser({
            ...loginResponse.user,
            ...loginResponse.userAuthStatus
          });
          navigate(getRedirectPath(loginResponse.userAuthStatus));
        },
        (err) => {
          const error =
            err.apiError?.message ||
            'Something went wrong. Please try again later.';
          setErrorMessage(error);
        }
      )
      .finally(() => setIsLoading(false));
  };

  const contactLink = (
    <a
      href={CONTACT_URL}
      target='_blank'
      rel='noopener noreferrer'
      className={INLINE_LINK}
    >
      contact our team
    </a>
  );

  return (
    <AuthLayout>
      <Helmet>
        <title>Japeto - Login</title>
      </Helmet>
      <h1 className='m-0 text-center text-2xl font-extrabold tracking-[-0.5px]'>
        Log in to your account
      </h1>
      <form
        className='mt-[26px] flex flex-col gap-3'
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <Controller
          name='email'
          control={control}
          rules={{ required: 'Email is required' }}
          render={({ field }) => (
            <CustomInput
              label='Email'
              type='email'
              containerClassName=''
              inputSize='xl'
              {...field}
              error={errors.email}
              fullWidth
            />
          )}
        />
        <Controller
          name='password'
          control={control}
          render={({ field }) => (
            <PasswordField
              {...field}
              error={errors.password}
              inputSize='xl'
              onSubmit={() => handleSubmit(onSubmit)()}
              fullWidth
            />
          )}
        />
        <AuthErrorAlert message={errorMessage} />
        <Button
          type='submit'
          loading={isLoading}
          size='xl'
          rounded='xl'
          className='mt-1 w-full'
        >
          Log in
        </Button>
        <div className='text-center text-[13.5px]'>
          <NavLink to='/forgot-password' className={INLINE_LINK}>
            Forgot password?
          </NavLink>
        </div>
        <p className='m-0 text-center text-[13px] leading-normal text-auth-muted'>
          Accounts are set up by invitation. Ask your organisation&apos;s admin,
          or {contactLink}.
        </p>
      </form>
    </AuthLayout>
  );
}
